import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import Button from "src/components/Button/Button";
import ImageLink from "src/components/ImageWrapper/ImageLink";
import { useHubCardLivePreview } from "src/hooks/useHubCardLivePreview";
import { mapHomeCarouselSlide } from "src/utils/mapHomeCarousel";
import { homeCarouselLivePreviewRequestHandler } from "src/utils/homeCarouselLivePreviewHandler";

/**
 * Payload Live Preview iframe target for `home-carousel`.
 * Renders one MoreThanShows-style slide so CMS editors can preview card content.
 * Always available for CMS editors — independent of REACT_APP_PAYLOAD_SOURCES.
 */
const HomeCarouselCardPreview = () => {
  const { t, i18n } = useTranslation();
  const { card, isLoading } = useHubCardLivePreview(
    "home-carousel",
    {
      depth: 0,
      mapDoc: mapHomeCarouselSlide,
      requestHandler: homeCarouselLivePreviewRequestHandler,
      forceEnabled: true,
    },
    i18n.language
  );

  return (
    <PreviewPage>
      {isLoading ? (
        <div className="preview-status">
          <Spinner animation="border" variant="light" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </Spinner>
        </div>
      ) : !card ? (
        <div className="preview-status">
          <p>{t("homepage.preview.waitingForDocument")}</p>
        </div>
      ) : (
        <SlideCard>
          <ImageLink
            to={card.url || "#"}
            imgSrc={card.image}
            alt={card.title}
            className={
              card.position === "left"
                ? "slide-header-image left"
                : "slide-header-image"
            }
          />
          <section>
            <h5>{card.title}</h5>
            <div className="slide-content-active">
              {card.text ? <p>{card.text}</p> : null}
              {card.url ? (
                <Button
                  bgColor="var(--red)"
                  color="var(--white)"
                  hoverColor="var(--white)"
                  hoverBgColor="var(--black)"
                  size="small"
                  fluid
                  href={card.url}
                  label={t("button.check-it-out")}
                />
              ) : null}
            </div>
          </section>
        </SlideCard>
      )}
    </PreviewPage>
  );
};

const PreviewPage = styled.div`
  min-height: 100vh;
  background: var(--black);
  color: var(--white);
  padding: 2rem 1rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  box-sizing: border-box;

  .preview-status {
    width: 100%;
    text-align: center;
    color: var(--mutedwhite);
    padding: 3rem 1rem;
  }
`;

const SlideCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  color: var(--black);
  border-radius: 15px;
  border: 1px solid var(--black);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .slide-header-image {
    flex-shrink: 0;
    width: 100%;
    height: auto;
    overflow: hidden;

    & > img {
      min-height: 280px;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }

    &.left > img {
      object-position: left;
    }
  }

  & > section {
    padding: 12px 14px 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex-grow: 1;

    & > h5 {
      text-align: center;
      text-transform: uppercase;
      margin-bottom: 10px;
    }

    .slide-content-active {
      max-height: none;
      opacity: 1;

      & > p {
        font-size: 14px;
        line-height: 20px;
        font-weight: 400;
        text-align: left;
        margin: 0 0 12px;
        white-space: pre-line;
      }

      & > a {
        display: block;
      }
    }
  }
`;

export default HomeCarouselCardPreview;
